import { asError, getLogger } from '@trimble/logging';
import { Accessor, createMemo, createSignal } from 'solid-js';
import { ControlMenuItemType } from '../control-menu';
import { ActionMenuItem, RadioGroupMenuItem } from '../control-menu/types';

export interface UseLastToolSelectionOptions {
  items: Accessor<ControlMenuItemType[]>;
  storageKey: Accessor<string>;
}

export interface UseLastToolSelectionResult {
  /** Items wrapped so any selection is recorded for later replay. */
  readonly menuItems: Accessor<ControlMenuItemType[]>;
  /** Replays the last recorded selection, falling back to the first actionable item. */
  activateLastSelection(): void;
}

const isActionReplayable = (item: ActionMenuItem): boolean =>
  item.replayable !== false;

const getDefaultItemId = (items: ControlMenuItemType[]): string | null => {
  for (const item of items) {
    if (item.type === 'radiogroup' && !item.disabled) {
      const firstOption = item.options[0];
      if (firstOption) return firstOption.id;
    }
    if (item.type === 'action' && !item.disabled && isActionReplayable(item)) {
      return item.id;
    }
  }
  return null;
};

const findRadioGroupForOptionId = (
  items: ControlMenuItemType[],
  optionId: string
): RadioGroupMenuItem | undefined => {
  for (const item of items) {
    if (
      item.type === 'radiogroup' &&
      item.options.some((option) => option.id === optionId)
    ) {
      return item;
    }
    if (item.type === 'action' && item.children) {
      const nested = findRadioGroupForOptionId(item.children, optionId);
      if (nested) return nested;
    }
    if (item.type === 'checkbox' && item.children) {
      const nested = findRadioGroupForOptionId(item.children, optionId);
      if (nested) return nested;
    }
  }
  return undefined;
};

const findActionItemById = (
  items: ControlMenuItemType[],
  itemId: string
): ActionMenuItem | undefined => {
  for (const item of items) {
    if (item.type === 'action' && item.id === itemId) {
      return item;
    }
    if (item.type === 'action' && item.children) {
      const nested = findActionItemById(item.children, itemId);
      if (nested) return nested;
    }
    if (item.type === 'checkbox' && item.children) {
      const nested = findActionItemById(item.children, itemId);
      if (nested) return nested;
    }
  }
  return undefined;
};

const isReplayableItemId = (
  items: ControlMenuItemType[],
  itemId: string
): boolean => {
  if (findRadioGroupForOptionId(items, itemId)) {
    return true;
  }

  const actionItem = findActionItemById(items, itemId);
  return actionItem ? isActionReplayable(actionItem) : false;
};

const readStoredSelection = (storageKey: string): string | null => {
  try {
    return sessionStorage.getItem(storageKey) ?? null;
  } catch (error) {
    getLogger().warn(
      'Failed to read split dropdown stored selection',
      { storageKey },
      asError(error)
    );
    return null;
  }
};

/**
 * @intent Tracks the last tool the user picked from a split dropdown so the
 * primary (icon) button can replay it. Persists the choice in sessionStorage
 * and wraps menu items so nested selections record their owning action.
 */
export const useLastToolSelection = (
  options: UseLastToolSelectionOptions
): UseLastToolSelectionResult => {
  const [lastSelectedItemId, setLastSelectedItemId] = createSignal<
    string | null
  >(readStoredSelection(options.storageKey()));

  const recordSelection = (itemId: string): void => {
    setLastSelectedItemId(itemId);
    try {
      sessionStorage.setItem(options.storageKey(), itemId);
    } catch (error) {
      getLogger().warn(
        'Failed to store split dropdown selection',
        { storageKey: options.storageKey(), itemId },
        asError(error)
      );
    }
  };

  const wrapItemsForSelectionTracking = (
    items: ControlMenuItemType[],
    parentActionId?: string,
    isParentActionReplayable: boolean = true
  ): ControlMenuItemType[] =>
    items.map((item) => {
      if (item.type === 'radiogroup') {
        return {
          ...item,
          setValue: (value: string): void => {
            if (parentActionId) {
              if (isParentActionReplayable) {
                recordSelection(parentActionId);
              }
            } else {
              recordSelection(value);
            }
            item.setValue(value);
          },
        };
      }

      if (item.type === 'action') {
        const replayable = isActionReplayable(item);
        return {
          ...item,
          onClick: (): void => {
            if (replayable) {
              recordSelection(item.id);
            }
            item.onClick();
          },
          children: item.children
            ? wrapItemsForSelectionTracking(item.children, item.id, replayable)
            : undefined,
        };
      }

      if (item.type === 'toggle') {
        return {
          ...item,
          setValue: (value: boolean): void => {
            if (parentActionId && isParentActionReplayable) {
              recordSelection(parentActionId);
            }
            item.setValue(value);
          },
        };
      }

      if (item.type === 'checkbox') {
        return {
          ...item,
          setValue: (value: boolean): void => {
            if (parentActionId && isParentActionReplayable) {
              recordSelection(parentActionId);
            }
            item.setValue(value);
          },
          children: item.children
            ? wrapItemsForSelectionTracking(
                item.children,
                parentActionId,
                isParentActionReplayable
              )
            : undefined,
        };
      }

      return item;
    });

  const menuItems = createMemo((): ControlMenuItemType[] =>
    wrapItemsForSelectionTracking(options.items())
  );

  const activateItem = (itemId: string): void => {
    const items = menuItems();
    const radioGroup = findRadioGroupForOptionId(items, itemId);
    if (radioGroup) {
      if (radioGroup.disabled) return;
      radioGroup.setValue(itemId);
      return;
    }

    const actionItem = findActionItemById(items, itemId);
    if (actionItem) {
      if (actionItem.disabled) return;
      actionItem.onClick();
    }
  };

  const activateLastSelection = (): void => {
    const items = options.items();
    const storedItemId = lastSelectedItemId();
    const targetId =
      storedItemId && isReplayableItemId(items, storedItemId)
        ? storedItemId
        : getDefaultItemId(items);
    if (targetId) {
      activateItem(targetId);
    }
  };

  return { menuItems, activateLastSelection };
};
